import React from 'react';
import styles from './InstagramLogo.module.css';

// Real official Instagram brand asset — fetched from Meta's Brand Resource
// Center (https://www.meta.com/brand/resources/instagram/icons/, "01 Static
// Glyph / 01 Gradient Glyph": the official Instagram gradient glyph) and
// staged by the Workspace Coordinator at
// .claude/temp/product/design/grant-access/assets/ before this component was
// built (PRD grant-access-connector-branding, I1/I4/I5; TRD R1.1/R1.3/D9/D10
// -- this replaces the pre-existing hand-drawn assets/icons/instagram.svg,
// whose gradient stops do not even match the PRD's own approximate reference
// stops, confirming it was a hand-drawn approximation, not a sourced asset;
// it is left untouched for its own existing internal-dialog use in
// MetaAuth.jsx). Source file: "IG_brand_asset_pack_2023.zip" -> 01 Static
// Glyph/01 Gradient Glyph/Instagram_Glyph_Gradient.png (native 5000x5000;
// the pack's same-named .svg is a ~10.8MB vectorized mesh-gradient export --
// far too large to bundle as a static web asset -- so the PNG export was used
// instead and downscaled with high-quality resampling to 512x512, a purely
// resolution-preserving change that alters no proportions or colors).
// Rendered as an opaque asset reference (<img src>), never inlined as raw
// SVG markup and never touched with a CSS fill/filter/color override -- an
// <img> physically cannot be recolored by CSS, unlike an inlined <svg> (same
// rationale as GoogleAnalyticsLogo.jsx).
import instagramGlyphAsset from '../../../assets/icons/instagram-glyph.png';

// Published minimum render size for the Instagram glyph (PRD I2): never
// smaller than 29x29px. Below this floor, a one-time dev-console warning is
// logged (not a hard clamp), mirroring GoogleAnalyticsLogo.jsx's pattern.
const MIN_SIZE = 29;
let hasWarnedSize = false;

// Instagram's clear-space rule (PRD I2): maintain clear space around the
// glyph equal to at least 1/2 of the glyph's own size on all sides.
// Implemented as the component's own internal padding (relative to the
// default render size) rather than left for each call site to remember --
// see InstagramLogo.module.css.

/**
 * InstagramIcon — the icon-only Instagram gradient glyph, real asset,
 * gradient/proportions locked (never recolored or reshaped).
 *
 * @param {number}  size      - Render size in px, square (default 32,
 *                              matching the connector-tile grid's icon size).
 * @param {string}  href        - Optional URL to wrap the mark in a link.
 * @param {string}  className   - Optional extra class on the outer wrapper.
 * @param {object}  style       - Optional inline style overrides on the <img>.
 * @param {object}  wrapperStyle - Optional inline style overrides on the
 *                                 outer wrapper (e.g. to zero out the default
 *                                 clear-space padding when embedding inside a
 *                                 container that already provides its own
 *                                 padding, like the connector-tile grid's
 *                                 iconBtn -- same escape hatch this file's
 *                                 header comment describes for className).
 */
export const InstagramIcon = ({ size = 32, href, className, style, wrapperStyle }) => {
  if (size < MIN_SIZE && !hasWarnedSize && process.env.NODE_ENV !== 'production') {
    hasWarnedSize = true;
    // eslint-disable-next-line no-console
    console.warn(
      `InstagramIcon: rendered at ${size}px, below the published ${MIN_SIZE}px minimum (PRD grant-access-connector-branding, I2).`
    );
  }

  const mark = (
    <img
      src={instagramGlyphAsset}
      alt="Instagram"
      width={size}
      height={size}
      style={style}
    />
  );

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram"
      className={[styles.markWrapper, className].filter(Boolean).join(' ')}
      style={wrapperStyle}
    >
      {mark}
    </a>
  ) : (
    <span className={[styles.markWrapper, className].filter(Boolean).join(' ')} style={wrapperStyle}>{mark}</span>
  );
};

// Default export mirrors the icon-only mark, same convention as
// GoogleAnalyticsLogo.jsx's default export.
const InstagramLogo = InstagramIcon;

export default InstagramLogo;
